import React, { useState } from 'react';

import { CustomeTextField } from '../../../generic/CustomeTextField';
import { ILegalTransactionGwGEntry } from '../../../Interfaces/ILegalTransactionGwGEntry';
import { DialogStructure } from '../../../core/DialogStructure';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { DocumentOverview } from '../../../Document/DocumentOverview';
import { IDocument } from '../../../Interfaces/IDocument';
import { useGetSystemMail } from '../../../hooks/data';
import { GwGRejectDialog } from './GwGRejectDialog';

interface IProps {
  open: boolean;
  entry: ILegalTransactionGwGEntry | null;
  setEntry: React.Dispatch<
    React.SetStateAction<ILegalTransactionGwGEntry | null>
  >;
  entryType: 'payment' | 'demand' | null;
  onSave: () => void;
  onCancel: () => void;
  isEditMode?: boolean;
  documentArray: IDocument[];
  setDocumentArray: (array: IDocument[]) => void;
}

export const GwGEntryDialog: React.FC<IProps> = ({
  open,
  entry,
  setEntry,
  entryType,
  onSave,
  onCancel,
  isEditMode = false,
  documentArray,
  setDocumentArray,
}) => {
  const [isOpenReject, setIsOpenReject] = useState(false);
  const [rejectSubject, setRejectSubject] = useState('');
  const [rejectBody, setRejectBody] = useState('');

  const { systemMail: rejectTemplate } = useGetSystemMail(41);
  const { systemMail: acceptTemplate } = useGetSystemMail(42);

  const handleOpenReject = () => {
    setRejectSubject(entry?.mailSubject || rejectTemplate?.Subject || '');
    setRejectBody(entry?.mailBody || rejectTemplate?.Body || '');
    setIsOpenReject(true);
  };
  const handleRejectSave = () => {
    setEntry(prev =>
      prev
        ? {
            ...prev,
            idLegalTransactionGwGEntryState: 3,
            mailSubject: rejectSubject,
            mailBody: rejectBody,
          }
        : prev
    );
    setIsOpenReject(false);
  };
  const handleAccept = () => {
    setEntry(prev =>
      prev
        ? {
            ...prev,
            idLegalTransactionGwGEntryState: 2,
            mailSubject: acceptTemplate?.Subject || '',
            mailBody: acceptTemplate?.Body || '',
          }
        : prev
    );
  };

  const handleStatusChange = (newVal: 1 | 2 | 3) => {
    if (newVal === 3) {
      handleOpenReject();
      return;
    }
    if (newVal === 2) {
      handleAccept();
      return;
    }
    setEntry(prev =>
      prev ? { ...prev, idLegalTransactionGwGEntryState: 1 } : prev
    );
  };

  if (!entry) return null;

  return (
    <>
      <GwGRejectDialog
        open={isOpenReject}
        subject={rejectSubject}
        body={rejectBody}
        setSubject={setRejectSubject}
        setBody={setRejectBody}
        onSave={handleRejectSave}
        onCancel={() => setIsOpenReject(false)}
      />

      <DialogStructure
        open={open}
        title={
          isEditMode
            ? 'Eintrag bearbeiten'
            : entryType === 'payment'
              ? 'Neue Zahlung'
              : 'Neue Ablöseforderung'
        }
        onSave={onSave}
        onCancel={onCancel}
        titleVariant={'h6'}
        maxWidth={'sm'}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CustomeTextField
              label='Titel'
              attr='title'
              object={entry}
              setObject={setEntry}
              type='string'
              required
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size='small'>
              <InputLabel>Status</InputLabel>
              {entry.currentRate !== null ? (
                <Select
                  value={entry.wasPaid ? 'paid' : 'unpaid'}
                  label='Status'
                  onChange={e => {
                    const val = e.target.value;
                    setEntry(prev =>
                      prev
                        ? {
                            ...prev,
                            wasPaid: val === 'paid',
                          }
                        : prev
                    );
                  }}
                >
                  <MenuItem value='unpaid'>Nicht bezahlt</MenuItem>
                  <MenuItem value='paid'>Bezahlt</MenuItem>
                </Select>
              ) : (
                <Select
                  value={entry.idLegalTransactionGwGEntryState || 1}
                  label='Status'
                  onChange={e =>
                    handleStatusChange(e.target.value as 1 | 2 | 3)
                  }
                >
                  <MenuItem value={1}>Prüfung</MenuItem>
                  <MenuItem value={2}>Geprüft</MenuItem>
                  <MenuItem value={3}>Abgelehnt</MenuItem>
                </Select>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <CustomeTextField
              label='Betrag'
              attr='currentValue'
              object={entry}
              setObject={setEntry}
              type='euro'
              required
            />
          </Grid>

          {entryType === 'demand' && (
            <>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Zinssatz'
                  attr='currentRate'
                  object={entry}
                  setObject={setEntry}
                  type='euro'
                />
              </Grid>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Nebenkosten'
                  attr='extraCosts'
                  object={entry}
                  setObject={setEntry}
                  type='euro'
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 5 }} />

        <DocumentOverview
          key={`gwg-document-${entry.idLegalTransactionGwGEntry}`}
          documentArray={documentArray}
          setDocumentArray={setDocumentArray}
          allowRemove
          allowUpload
          title='Dokumente'
        />
      </DialogStructure>
    </>
  );
};
